import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    google?: any;
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/**
 * Login screen — renders the Google Identity Services "Sign in with Google"
 * button. On success it POSTs the returned ID token to /api/auth/google; the
 * server verifies it, checks the email domain, and sets the session cookie.
 */
export default function LoginView({
  clientId,
  domain,
  onLogin,
}: {
  clientId: string;
  domain: string;
  onLogin: () => void;
}) {
  const btnRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const handleCredential = async (resp: { credential?: string }) => {
      if (!resp?.credential) return;
      setBusy(true);
      setError(null);
      try {
        const r = await fetch('/api/auth/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: resp.credential }),
        });
        if (r.ok) {
          onLogin();
          return;
        }
        const j = await r.json().catch(() => ({}));
        setError(j.reason || 'Sign-in failed. Try again.');
      } catch {
        setError('Network error during sign-in.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    };

    const init = () => {
      if (cancelled || !window.google || !btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(btnRef.current, {
        theme: 'filled_blue',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
        logo_alignment: 'left',
        width: 280,
      });
    };

    let script = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (window.google) {
      init();
    } else if (script) {
      script.addEventListener('load', init);
    } else {
      script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', init);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener('load', init);
    };
  }, [clientId, onLogin]);

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">Content Engine</div>
        <h1 className="login__title">Sign in</h1>
        <p className="login__sub">
          Use your <strong>@{domain}</strong> Google account.
        </p>
        <div ref={btnRef} className="login__btn" aria-busy={busy} />
        {busy && <p className="login__hint">Signing in…</p>}
        {error && <p className="login__error">{error}</p>}
        <p className="login__foot">Access is restricted to {domain} team members.</p>
      </div>
    </div>
  );
}
